import React from "react";
import api from "../../api/axiosConfig";
import { refreshAccessToken } from "../../api/authSession";
import { endpoints } from "../../api/endpoints";
import { Header } from "../../components/Header/Headers";
import { getApiErrorMessage } from "../../utils/apiError";
import {
  getStoredAccessToken,
  isAccessTokenExpired,
} from "../../utils/authStorage";
import StationManagement from "./Components/StationManagement";
import AddStationModal from "./Components/AddStationModal";

interface StationFormData {
  stationType: string;
  stationName: string;
  ownershipType: string;
  city: string;
  state: string;
  pinCode: string;
  stationAddress: string;
  cgdName: string;
  pesoApprovalNumber: string;
  assignManager: string;
}

interface CreateStationPayload {
  name: string;
  address: string;
  cgd_name?: string;
  ownership_type?: string;
  peso_approval_number?: string;
  station_type: string;
  city: string;
  state: string;
  pincode: string;
  manager_id: string;
}

interface EditStationData extends StationFormData {
  id: string;
}

const getEditStationFormData = (
  station: EditStationData | null,
): StationFormData | undefined => {
  if (!station) {
    return undefined;
  }

  const { id, ...stationFormData } = station;

  return stationFormData;
};

const StationOnboardingSection: React.FC = () => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [editStation, setEditStation] = React.useState<EditStationData | null>(
    null,
  );

  const handleSaveStation = async (
    stationData: StationFormData,
  ): Promise<boolean> => {
    setIsSubmitting(true);
    setSaveError(null);

    const isTestingStation = stationData.stationType === "TESTING_CENTER";
    const trimmedPesoApprovalNumber = stationData.pesoApprovalNumber.trim();

    const payload: CreateStationPayload = {
      name: stationData.stationName.trim(),
      address: stationData.stationAddress.trim(),
      station_type: stationData.stationType,
      city: stationData.city.trim(),
      state: stationData.state.trim(),
      pincode: stationData.pinCode.trim(),
      manager_id: stationData.assignManager,
      ...(!isTestingStation
        ? {
            cgd_name: stationData.cgdName.trim(),
            ownership_type: stationData.ownershipType.trim(),
          }
        : {}),
      ...(isTestingStation && trimmedPesoApprovalNumber
        ? {
            peso_approval_number: trimmedPesoApprovalNumber,
          }
        : {}),
    };

    try {
      let accessToken = getStoredAccessToken();

      if (!accessToken || isAccessTokenExpired(accessToken)) {
        accessToken = await refreshAccessToken();
      }

      if (!accessToken) {
        throw new Error("Access token is not available. Please log in again.");
      }

      if (editStation) {
        await api.patch(endpoints.stations.update(editStation.id), payload, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      } else {
        await api.post(endpoints.stations.create, payload, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      }
      setRefreshKey((current) => current + 1);
      setEditStation(null);
      setModalOpen(false);
      return true;
    } catch (error: any) {
      const message = getApiErrorMessage(
        error,
        editStation
          ? "Failed to update station. Please try again."
          : "Failed to create station. Please try again.",
      );

      setSaveError(message);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditStation = (station: {
    id: string;
    stationTypeValue: string;
    name: string;
    city: string;
    state: string;
    pinCode: string;
    address: string;
    branchName: string;
    managerId: string;
    ownershipType?: string;
    pesoApprovalNumber?: string;
  }) => {
    setSaveError(null);
    setEditStation({
      id: station.id,
      stationType:
        station.stationTypeValue === "TESTING_CENTRE"
          ? "TESTING_CENTER"
          : station.stationTypeValue,
      stationName: station.name,
      city: station.city === "--" ? "" : station.city,
      state: station.state === "--" ? "" : station.state,
      pinCode: station.pinCode === "--" ? "" : station.pinCode,
      stationAddress: station.address === "--" ? "" : station.address,
      ownershipType:
        !station.ownershipType || station.ownershipType === "--"
          ? ""
          : station.ownershipType,
      cgdName: station.branchName === "--" ? "" : station.branchName,
      pesoApprovalNumber:
        !station.pesoApprovalNumber || station.pesoApprovalNumber === "--"
          ? ""
          : station.pesoApprovalNumber,
      assignManager: station.managerId,
    });
    setModalOpen(true);
  };

  return (
    <>
      <Header
        title="Station Onboarding"
        breadcrumb="Admin Console / Station Onboarding"
        isStationsPage
        onAddStation={() => {
          setEditStation(null);
          setSaveError(null);
          setModalOpen(true);
        }}
      />
      <StationManagement
        refreshKey={refreshKey}
        onEditStation={handleEditStation}
      />
      <AddStationModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSaveError(null);
          setEditStation(null);
        }}
        onSave={handleSaveStation}
        isSubmitting={isSubmitting}
        errorMessage={saveError}
        onClearError={() => setSaveError(null)}
        initialData={getEditStationFormData(editStation)}
        isEditMode={!!editStation}
      />
    </>
  );
};

export default StationOnboardingSection;
